import { z } from 'zod';
import { assertTypes, type NoNewFields } from '../primitives';

/** Swatches the admin pickers offer. `hex` is what gets stored against the uuid. */
export const POS_BUTTON_COLORS = [
  { hex: '#FFFFFF', label: 'White' },
  { hex: '#7B52B7', label: 'Purple' },
  { hex: '#4691F8', label: 'Blue' },
  { hex: '#E46B6B', label: 'Red' },
  { hex: '#53A652', label: 'Green' },
  { hex: '#D07520', label: 'Orange' },
] as const;

export const DEFAULT_POS_BUTTON_COLOR = '#FFFFFF';

/** Uppercase 6-digit hex, e.g. `#7B52B7`. Pickers only offer `POS_BUTTON_COLORS`. */
export const zPosButtonColor = z
  .string()
  .regex(/^#[0-9A-F]{6}$/, 'Must be a hex color like #7B52B7');

/**
 * The POS button tints for one restaurant's menu items and modifiers, keyed by their `uuid`.
 *
 * Kept out of the menu (and so out of `menuVersions`) because a tint is POS presentation, not menu
 * content: it is edited by a different gesture, must not make a menu save snapshot a new version,
 * must not be undone by a menu revert, and never reaches the consumer apps. Menu documents are also
 * saved whole from the client, so a field living there is stripped by any app build that predates it.
 *
 * Both records are sparse — only a uuid given a non-default tint is stored, so the default lives in
 * `getPosButtonColor`, not in the data. Keys are never cascaded on a menu delete: an orphan is
 * invisible (reads join by uuid) and `_pruneOrphanedPosButtonColors` clears them when someone cares.
 */
export const posButtonColorsSchema = z.object({
  restaurantId: z.string(),
  /** Menu item uuid → hex. Absent = `DEFAULT_POS_BUTTON_COLOR`. */
  menuItems: z.record(z.string(), zPosButtonColor).default({}),
  /** Modifier uuid → hex. Absent = `DEFAULT_POS_BUTTON_COLOR`. */
  modifiers: z.record(z.string(), zPosButtonColor).default({}),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type PosButtonColors = z.output<typeof posButtonColorsSchema>;

/** Strict schema */
export const posButtonColorsStrictSchema = posButtonColorsSchema.safeExtend({
  menuItems: z.record(z.guid(), zPosButtonColor),
  modifiers: z.record(z.guid(), zPosButtonColor),
});

/** Convex schema */
export const posButtonColorsCvxSchema = posButtonColorsSchema.safeExtend({
  // Required, unlike the base tier: the module writes both records on every save, so a reader
  // never has to distinguish "no colours yet" from "field missing".
  menuItems: z.record(z.string(), zPosButtonColor),
  modifiers: z.record(z.string(), zPosButtonColor),
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type PosButtonColorsCvx = z.output<typeof posButtonColorsCvxSchema>;
assertTypes<NoNewFields<PosButtonColors, PosButtonColorsCvx>>();

/** A row carrying the tint a client is about to save, whatever else the row is. */
type PosButtonColorRow = { uuid: string; posButtonColor: string };

/**
 * The tint stored for `uuid`, or the default. Storage is sparse, so every client resolves the
 * default through here rather than repeating a `??` at each read. `uuid` may be undefined for a
 * row that does not exist yet, which resolves the default too.
 */
export const getPosButtonColor = (
  stored: Record<string, string> | undefined,
  uuid: string | undefined,
) => (uuid === undefined ? undefined : stored?.[uuid]) ?? DEFAULT_POS_BUTTON_COLOR;

/** Rows whose tint differs from what is stored: the payload for the `set` mutation. */
export const getChangedPosButtonColors = (
  rows: PosButtonColorRow[],
  stored: Record<string, string> | undefined,
) =>
  Object.fromEntries(
    rows
      .filter((row) => row.posButtonColor !== getPosButtonColor(stored, row.uuid))
      .map((row) => [row.uuid, row.posButtonColor]),
  );

/** The color every row shares, or undefined when rows disagree or there are none. */
export const getSharedPosButtonColor = (
  rows: { posButtonColor?: string }[],
): string | undefined => {
  const first = rows[0];
  if (!first) return undefined;
  return rows.every((row) => row.posButtonColor === first.posButtonColor)
    ? first.posButtonColor
    : undefined;
};
